import classNames from "classnames";
import { DateTime } from "luxon";
import { useRouter } from "next/router";
import React, {
  useState,
  useMemo,
  useCallback,
  useContext,
  useEffect,
} from "react";
import { FormattedMessage, useIntl } from "react-intl";

import BackgroundWavesHorizontal from "../../../public/svg/background-waves-horizontal.svg";
import BackgroundWavesMobile from "../../../public/svg/background-waves-mobile.svg";
import { CalendarContext } from "../../contexts/CalendarContext";
import { CartContext } from "../../contexts/CartContext";
import { ThemeContext } from "../../contexts/ThemeContext";
import {
  DateSelection,
  extendedVariantValidityDateComparator,
  getCheapestPriceFromVariants,
  getExtendedCartMerchandise,
  hasBothOpenAndFixedDateVariants,
  ProductType,
  Tag,
} from "../../ecommerce/product";
import { getProductSchedule, getStartingDate } from "../../ecommerce/schedule";
import useRouteChangeIndicator from "../../hooks/useRouteChangeIndicator";
import { isLocale } from "../../lib/i18n";
import { Product as ShopifyProduct } from "../../types/shopify";
import { ExtendedCartMerchandise } from "../../types/shopify-components";
import aspectRatioPadding from "../../utils/aspect-ratio-padding";
import ContentToggle from "../ContentToggle";
import LoadingSpinner from "../LoadingSpinner";

import DynamicPricingCalendar from "./DynamicPricingCalendar";
import EcommerceProductDayPricingToggle from "./EcommerceProductDayPricingToggle";
import EcommerceProductImage from "./EcommerceProductImage";
import css from "./Product.module.scss";
import ProductDefaultView from "./ProductDefaultView";
import SeasonPassProductActions from "./SeasonPassProductActions";

export const TEST_ID_CALENDAR_FAILED = "ecommerce-product-calendar-failed";
export const testIdTitle = "ecommerce-product-title";
export const testIdFixedDay = "ecommerce-product-fixed-day";
export const testIdSpecialEvent = "ecommerce-product-special-event";

export interface ProductProps {
  product: ShopifyProduct;
}

const Product: React.FC<ProductProps> = ({ product }) => {
  const {
    descriptionHtml,
    handle,
    productType,
    images,
    options,
    tags,
    title: productTitle,
  } = product;
  const {
    calendarRows,
    hasFailed: hasCalendarFailed,
    isLoading: calendarLoading,
  } = useContext(CalendarContext);
  const { localItems, resetQuantities } = useContext(CartContext);
  const { styles } = useContext(ThemeContext);
  const { locale, formatMessage } = useIntl();
  const { events } = useRouter();
  const productsSlug = formatMessage({ id: "appPath.products" });
  const { isChangingRoute: isChangingToProductsPage } = useRouteChangeIndicator(
    `/${locale}/${productsSlug}`,
  );

  const leadooName = useMemo(() => {
    const leadooTags = tags.filter(tag => tag.startsWith("leadoo:"));
    if (leadooTags.length === 0) {
      return null;
    }
    const [_, name] = leadooTags[0].split(":");
    return name;
  }, [tags]);

  const usesCalendar = useMemo(() => {
    return tags.some(tag => tag.startsWith("calendar_name_"));
  }, [tags]);

  const schedule = useMemo(() => {
    if (calendarLoading) {
      return [];
    }

    return getProductSchedule(product, calendarRows).map(row => ({
      ...row,
      date: DateTime.fromISO(row.date).startOf("day"),
    }));
  }, [calendarRows, calendarLoading, product]);

  const scheduleLookup = useMemo(() => {
    return schedule.map(row => row.date.toMillis());
  }, [schedule]);

  const merchandises: ExtendedCartMerchandise[] = useMemo(() => {
    return product.variants.edges.map(edge =>
      getExtendedCartMerchandise(edge.node, {
        title: productTitle,
        handle,
        productType,
        tags,
      }),
    );
  }, [handle, product.variants.edges, productTitle, productType, tags]);

  const usesFixedDayPricing = useMemo(
    () => merchandises.some(variant => variant.validityDate),
    [merchandises],
  );
  const [selectedDayPricing, setSelectedDayPricing] = useState<DateSelection>(
    usesFixedDayPricing ? DateSelection.FIXED : DateSelection.OPEN,
  );
  const [selectedDate, setSelectedDate] = useState<DateTime | null>(null);
  const isFixedSelection = selectedDayPricing === DateSelection.FIXED;

  const startingDate = useMemo(() => getStartingDate(schedule), [schedule]);

  const [selectedDateSpecialEventTitle, selectedDateSpecialEventContent] =
    useMemo(() => {
      const found = schedule.find(
        ({ date }) => date.toMillis() === selectedDate?.toMillis(),
      );
      if (isLocale(locale)) {
        return [
          found?.specialEvent?.[locale]?.title,
          found?.specialEvent?.[locale]?.content,
        ];
      }
      return [undefined, undefined];
    }, [selectedDate, schedule, locale]);

  const cheapestFixedPrice = useMemo(
    () =>
      getCheapestPriceFromVariants(
        merchandises,
        DateSelection.FIXED,
        startingDate,
        scheduleLookup,
      ),
    [scheduleLookup, startingDate, merchandises],
  );
  const cheapestOpenPrice = useMemo(
    () => getCheapestPriceFromVariants(merchandises, DateSelection.OPEN),
    [merchandises],
  );

  const variantsToShowInLineItems = useMemo(
    () =>
      merchandises
        .filter(({ dateSelection, validityDate, tags }) => {
          if (isFixedSelection) {
            if (selectedDate && validityDate) {
              let threshold = 0;
              const equalDate = selectedDate.equals(
                validityDate.startOf("day"),
              );
              const timeSelection = tags.includes(Tag.TIME_SELECTION);
              const thresholdTag = tags.find(tag =>
                tag.startsWith(Tag.THRESHOLD_PREFIX),
              );
              if (thresholdTag) {
                const parts = thresholdTag.split("_");
                if (parts.length === 4) {
                  const strValue = parts[2];
                  const minutes = Math.abs(parseInt(strValue));
                  if (!Number.isNaN(minutes)) {
                    threshold = minutes;
                  }
                }
              }
              return timeSelection
                ? equalDate &&
                    validityDate.plus({ minutes: threshold }) >= DateTime.now()
                : equalDate;
            }
            return false;
          } else {
            return dateSelection === DateSelection.OPEN || !validityDate;
          }
        })
        // sort variants shown based on their validity date
        .sort(extendedVariantValidityDateComparator),
    [isFixedSelection, selectedDate, merchandises],
  );

  const lineItemTotalPrice = useMemo(
    () =>
      variantsToShowInLineItems.reduce((total, variant) => {
        const item = localItems.find(
          item => item.merchandise.id === variant.id,
        );
        const price = Number(variant.price.amount);
        if (item && !Number.isNaN(price)) {
          return total + (item.input.quantity ?? 0) * price;
        }
        return total;
      }, 0),
    [localItems, variantsToShowInLineItems],
  );

  const variantsToShowInCalendar = useMemo(
    () =>
      isFixedSelection
        ? merchandises.filter(({ validityDate }) => {
            return !!validityDate;
          })
        : [],
    [isFixedSelection, merchandises],
  );

  const ecommerceProductDayPricingToggle = useMemo(
    () =>
      cheapestFixedPrice && cheapestOpenPrice ? (
        <EcommerceProductDayPricingToggle
          fixedPrice={cheapestFixedPrice}
          openPrice={cheapestOpenPrice}
          selectedDayPricing={selectedDayPricing}
          handleDayPricingChange={setSelectedDayPricing}
        />
      ) : null,
    [cheapestFixedPrice, cheapestOpenPrice, selectedDayPricing],
  );

  const handleDaySelect = useCallback(
    (date: DateTime): void => {
      setSelectedDate(date);
      const ids = merchandises.map(variant => variant.id);
      resetQuantities(ids);
    },
    [resetQuantities, merchandises],
  );

  const dynamicPricingCalendar = useMemo(
    () =>
      schedule.length > 0 ? (
        <DynamicPricingCalendar
          schedule={schedule}
          variants={variantsToShowInCalendar}
          handleDaySelect={handleDaySelect}
        />
      ) : null,
    [handleDaySelect, schedule, variantsToShowInCalendar],
  );

  const calendarFailed = (
    <p
      className="tc mv4 pa3 ba b--moon-gray"
      data-testid={TEST_ID_CALENDAR_FAILED}
    >
      <FormattedMessage id="openingHours.fetchingOpeningHoursFailed" />
    </p>
  );

  const hasFixedAndOpenDayPricing = useMemo(
    () => hasBothOpenAndFixedDateVariants(options),
    [options],
  );
  const productImage = useMemo(
    () =>
      images.edges.length
        ? (images.edges.map(edge => edge.node.url)[0] as string)
        : undefined,
    [images.edges],
  );

  const onRouteChangeStart = useCallback(() => {
    resetQuantities(merchandises.map(variant => variant.id));
  }, [resetQuantities, merchandises]);

  useEffect(() => {
    // Handler changed, re-apply handler
    events.off("routeChangeStart", onRouteChangeStart);
    events.on("routeChangeStart", onRouteChangeStart);

    return (): void => {
      events.off("routeChangeStart", onRouteChangeStart);
    };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [onRouteChangeStart]);

  const getProductView = () => {
    switch (product.productType) {
      case ProductType.COMPOSITE_PARENT: {
        const [parent, ...children] = merchandises;
        return (
          <ProductDefaultView
            isFixedSelection={isFixedSelection}
            selectedDate={selectedDate}
            variantsToShowInLineItems={[parent]}
            connectedVariants={children}
            productTitle={productTitle}
            lineItemTotalPrice={lineItemTotalPrice}
            isChangingToProductsPage={isChangingToProductsPage}
            specialEventTitle={selectedDateSpecialEventTitle}
            specialEventContent={selectedDateSpecialEventContent}
          />
        );
      }
      case ProductType.SEASON_CARD: {
        return (
          <SeasonPassProductActions
            isChangingToProductsPage={isChangingToProductsPage}
            merchandises={merchandises}
          />
        );
      }
      default: {
        return (
          <ProductDefaultView
            isFixedSelection={isFixedSelection}
            selectedDate={selectedDate}
            variantsToShowInLineItems={variantsToShowInLineItems}
            productTitle={productTitle}
            lineItemTotalPrice={lineItemTotalPrice}
            isChangingToProductsPage={isChangingToProductsPage}
            specialEventTitle={selectedDateSpecialEventTitle}
            specialEventContent={selectedDateSpecialEventContent}
          />
        );
      }
    }
  };

  return (
    <section className="bg-white">
      <div className="flex flex-column flex-row-ns pv4 pv5-l">
        <article className="w-50-ns ph3 pl5-l order-2">
          <h1
            className={`${styles.heading2} mt3 mb3 mt0-ns mb2-ns`}
            data-testid={testIdTitle}
          >
            {productTitle}
          </h1>
          {hasFixedAndOpenDayPricing && dynamicPricingCalendar && (
            <div className="pt4 dn-ns">{ecommerceProductDayPricingToggle}</div>
          )}
          <div
            className={`${styles.paragraph3} nested-contained-copy-separator mt2 mt3-ns pt2-ns`}
          >
            <div dangerouslySetInnerHTML={{ __html: descriptionHtml }}></div>
          </div>
          {dynamicPricingCalendar ? (
            <div
              className="mv4 dn-ns"
              data-testid="ecommerce-product-see-opening-hours"
            >
              <ContentToggle
                title={formatMessage({ id: "contentToggle.seeOpeningHours" })}
                displayChildrenOnly={isFixedSelection}
              >
                {dynamicPricingCalendar}
              </ContentToggle>
            </div>
          ) : usesCalendar && hasCalendarFailed ? (
            <div className="db dn-ns">{calendarFailed}</div>
          ) : null}
          {getProductView()}
        </article>
        <aside className="w-100 w-50-ns ph3-ns order-1">
          {dynamicPricingCalendar ? (
            <div className="dn db-ns">
              {hasFixedAndOpenDayPricing && (
                <div className="pb4 ph3 w-100 center">
                  {ecommerceProductDayPricingToggle}
                </div>
              )}
              {dynamicPricingCalendar}
            </div>
          ) : usesCalendar && hasCalendarFailed ? (
            <div className="dn db-ns">{calendarFailed}</div>
          ) : usesCalendar && calendarLoading ? (
            <LoadingSpinner
              message={formatMessage({ id: "calendar.loading" })}
            />
          ) : (
            <div className="aspect-ratio aspect-ratio--1x1 relative bg-white white pointer-events-none">
              <EcommerceProductImage
                className="absolute top-0 left-0 w-100 h-100"
                productName={productTitle}
                imageSrc={productImage}
              />
              <div
                className="absolute bottom-0 left-0 w-100 dn-ns z-1"
                style={{ paddingTop: aspectRatioPadding(320, 30) }}
                aria-hidden="true"
              >
                <div className={css.overlay}>
                  <BackgroundWavesMobile />
                </div>
              </div>
              <div
                className="absolute top-0 right-0 w-100 h-100 translate-right-50 dn db-ns z-1"
                aria-hidden="true"
              >
                <div className={classNames(css.overlay, css.overlayDesktop)}>
                  <BackgroundWavesHorizontal />
                </div>
              </div>
            </div>
          )}
        </aside>
      </div>
      {leadooName !== null && (
        <div className="pb4">
          <ld-slot name={leadooName} />
        </div>
      )}
    </section>
  );
};

export default Product;
